/**
 * Calendar tab model.
 *
 * The work-order, shift and lift calendars are consolidated into a single
 * Calendar page with URL-driven tabs (?tab=…). These helpers keep the
 * visible tab set and the active tab permission-aware so a crafted ?tab=
 * value can never leave the MUI Tabs component on a tab the user is not
 * allowed to see (which would render a blank content area).
 */

export type CalendarTab = 'workorders' | 'shifts' | 'lifts' | 'courtesy' | 'tasks';

export interface CalendarTabPermissions {
  canViewWorkOrders: boolean;
  canViewLifts: boolean;
  /** Courtesy-car calendar (PF-120). Read = work_orders.read. Defaults off so existing exact lists stay valid. */
  canViewCourtesy?: boolean;
  /** Supervisor task calendar (#3671). Read = scheduling.exceptions. Defaults off like courtesy. */
  canViewSupervisorTasks?: boolean;
}

/**
 * Tabs the user may see, in canonical display order: Työmääräimet · Työvuorot · Nosturit · Sijaisautot · Tehtävät.
 *
 * Work orders requires `work_orders.read` (the legacy `bookings.read` grant counts as the same right); lifts requires `settings.view`
 * (matching the Equipment page's existing gate). Courtesy cars require
 * `work_orders.read`. The supervisor task calendar requires `scheduling.exceptions`
 * (the foreman's permission, as for the exceptions page). Shifts has no
 * dedicated read permission — the shift calendar is open to any authenticated
 * user today, so it is always included.
 */
export function getAllowedCalendarTabs({
  canViewWorkOrders,
  canViewLifts,
  canViewCourtesy = false,
  canViewSupervisorTasks = false,
}: CalendarTabPermissions): CalendarTab[] {
  const tabs: CalendarTab[] = [];
  if (canViewWorkOrders) tabs.push('workorders');
  tabs.push('shifts');
  if (canViewLifts) tabs.push('lifts');
  if (canViewCourtesy) tabs.push('courtesy');
  if (canViewSupervisorTasks) tabs.push('tasks');
  return tabs;
}

/**
 * Clamp a requested (URL) tab to one the user is actually allowed to see.
 * Falls back to the first allowed tab, or 'shifts' when nothing is allowed.
 */
export function resolveCalendarTab(
  requested: string | null,
  allowed: CalendarTab[],
): CalendarTab {
  if (requested && (allowed as string[]).includes(requested)) {
    return requested as CalendarTab;
  }
  return allowed[0] ?? 'shifts';
}
